import React from 'react';
import styled from 'styled-components';

import { ApiBalanceHistoryEvent } from '#/api/types';
import SpinningRoundedBorder from '#/components/basic/animations/SpinningRoundedBorder';
import LabelClickable from '#/components/basic/Button/LabelClickable';
import Link from '#/components/basic/Link/Link';
import { useSelectorActiveChain } from '#/components/hooks';
import { StargateExplorerDoubleIcon } from '#/components/wallet/icons/explorer/StargateExplorerDoubleIcon';
import { CoinIcon } from '#/config/coinIcons/CoinIcon';
import { getBlockExplorerTransactionUrl } from '#/config/configUtils';
import { useSelector } from '#/reduxState/store';

import { convertApiBridge } from '../bridge/crossChainMapping';
import { getStargateScannerTxUrl } from '../bridge/utils';
import { getChainIcon } from '../chains/chainIcons/chainIcons';

import { PendingStatus } from './PendingStatus';

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/** Static width for all icons to make vertical alignement look good */
const IconWrap = styled.div`
  display: inline-flex;
  width: 15px;
  align-items: center;
  justify-content: center;
`;

const DoubleIconWrap = styled.div`
  display: inline-flex;
  width: 23px;
  align-items: center;
  justify-content: center;
`;

const TableIcon = styled.img`
  height: 12px;
  vertical-align: inherit;
`;

/* SVG versions of Material Symbols Rounded "check_circle" (filled) and
   "warning". A font glyph never rasterizes to the exact same pixels as the
   12px chain/coin images next to it, so these render 1px misaligned on some
   display scales - fixed 12x12 SVGs match the images on every screen */
const SuccessIcon = () => (
  // viewBox cropped to the circle bounds so it renders exactly 12x12
  <svg width="12" height="12" viewBox="80 -880 800 800" fill="var(--green4)">
    <path d="m424-408-86-86q-11-11-28-11t-28 11q-11 11-11 28t11 28l114 114q12 12 28 12t28-12l226-226q11-11 11-28t-11-28q-11-11-28-11t-28 11L424-408Zm56 328q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
  </svg>
);

const WarningIcon = () => (
  // viewBox centers the triangle (ink 69.5..890.5 x, -840.5..-120 y)
  <svg width="12" height="12" viewBox="40 -920 880 880" fill="var(--red3)">
    <path d="M109-120q-11 0-20-5.5T75-140q-5-9-5.5-19.5T75-180l370-640q6-10 15.5-15t19.5-5q10 0 19.5 5t15.5 15l370 640q6 10 5.5 20.5T885-140q-5 9-14 14.5t-20 5.5H109Zm69-80h604L480-720 178-200Zm330.5-51.5Q520-263 520-280t-11.5-28.5Q497-320 480-320t-28.5 11.5Q440-297 440-280t11.5 28.5Q463-240 480-240t28.5-11.5Zm0-120Q520-383 520-400v-120q0-17-11.5-28.5T480-560q-17 0-28.5 11.5T440-520v120q0 17 11.5 28.5T480-360q17 0 28.5-11.5ZM480-460Z" />
  </svg>
);

export default function BalanceTransactionStatuses({
  transaction,
}: {
  transaction: ApiBalanceHistoryEvent;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const { activeChain } = useSelectorActiveChain();

  if (transaction.type === 'pendingDeposit') {
    return <PendingStatus transaction={transaction} />;
  }

  const transferStatus = (transaction.type === 'transferFromWallet' ||
    transaction.type === 'transferToWallet') &&
    transaction.katanaTxId && (
      <>
        {transaction.type === 'transferToWallet' && (
          <IconWrap>
            <SuccessIcon />
          </IconWrap>
        )}
        <IconWrap>
          <Link
            href={getBlockExplorerTransactionUrl(
              transaction.katanaTxId,
              activeChain
            )}
            $alignCenter
          >
            <CoinIcon symbol="KATANA" size="12px" />
          </Link>
        </IconWrap>
        {transaction.type === 'transferFromWallet' && (
          <IconWrap>
            <SuccessIcon />
          </IconWrap>
        )}
      </>
    );

  if (isMobile) {
    return (
      <Wrap>
        {transferStatus}

        {transaction.type === 'withdrawal' && (
          <>
            <IconWrap>
              {transaction.katanaTxStatus === 'failed' ? (
                <WarningIcon />
              ) : transaction.katanaTxStatus === 'pending' ? (
                <SpinningRoundedBorder size={12} inline />
              ) : (
                <SuccessIcon />
              )}
            </IconWrap>
            <IconWrap>
              <TableIcon src={getChainIcon('katanaChain')} />
            </IconWrap>
            {transaction.bridgeTarget !== 'katana.katana' &&
              transaction.katanaTxId && (
                <DoubleIconWrap>
                  <StargateExplorerDoubleIcon
                    chain="katanaChain"
                    chainType="target"
                    size={12}
                  />
                </DoubleIconWrap>
              )}
          </>
        )}

        {transaction.type === 'deposit' && (
          <>
            {convertApiBridge(transaction.bridgeSource).bridge ===
              'Stargate' && (
              <DoubleIconWrap>
                <StargateExplorerDoubleIcon
                  chain={convertApiBridge(transaction.bridgeSource).targetChain}
                  chainType="source"
                  size={12}
                />
              </DoubleIconWrap>
            )}
            {transaction.forwarderTxId && (
              <DoubleIconWrap>
                <StargateExplorerDoubleIcon
                  chain="ethereum"
                  chainType="source"
                  size={12}
                />
              </DoubleIconWrap>
            )}
            <IconWrap>
              <TableIcon src={getChainIcon('katanaChain')} />
            </IconWrap>
            {transaction.bridgeTxId || transaction.katanaTxId ? (
              <IconWrap>
                <SuccessIcon />
              </IconWrap>
            ) : (
              <LabelClickable type="error">Pending</LabelClickable>
            )}
          </>
        )}
      </Wrap>
    );
  }

  return (
    <Wrap>
      {transferStatus}

      {transaction.type === 'withdrawal' && (
        <>
          <IconWrap>
            {transaction.katanaTxStatus === 'failed' ? (
              <WarningIcon />
            ) : transaction.katanaTxStatus === 'pending' ? (
              <SpinningRoundedBorder size={12} inline />
            ) : (
              <SuccessIcon />
            )}
          </IconWrap>

          {transaction.katanaTxId ? (
            <IconWrap>
              <Link
                href={getBlockExplorerTransactionUrl(
                  transaction.katanaTxId,
                  activeChain
                )}
                $alignCenter
              >
                <TableIcon src={getChainIcon('katanaChain')} />
              </Link>
            </IconWrap>
          ) : null}

          {transaction.bridgeTarget !== 'katana.katana' &&
            transaction.katanaTxId && (
              <DoubleIconWrap>
                <Link
                  href={getStargateScannerTxUrl(transaction.katanaTxId)}
                  $alignCenter
                >
                  <StargateExplorerDoubleIcon
                    chain="ethereum"
                    chainType="target"
                    size={12}
                  />
                  {/* <img src={stargateBridgeIcon} /> */}
                </Link>
              </DoubleIconWrap>
            )}
        </>
      )}

      {transaction.type === 'deposit' && (
        <>
          {transaction.bridgeTxId && (
            <DoubleIconWrap>
              <Link
                href={getStargateScannerTxUrl(transaction.bridgeTxId)}
                $alignCenter
              >
                <StargateExplorerDoubleIcon
                  chainType="source"
                  chain={convertApiBridge(transaction.bridgeSource).targetChain}
                  size={12}
                />
              </Link>
            </DoubleIconWrap>
          )}

          {transaction.forwarderTxId && (
            <DoubleIconWrap>
              <Link
                href={getStargateScannerTxUrl(transaction.forwarderTxId)}
                $alignCenter
              >
                <StargateExplorerDoubleIcon
                  chain="ethereum"
                  chainType="source"
                  size={12}
                />
              </Link>
            </DoubleIconWrap>
          )}

          {/* Only not available for exchange.transfer */}
          {transaction.katanaTxId && (
            <IconWrap>
              <Link
                href={getBlockExplorerTransactionUrl(
                  transaction.katanaTxId,
                  activeChain
                )}
                $alignCenter
              >
                <TableIcon src={getChainIcon('katanaChain')} />
              </Link>
            </IconWrap>
          )}

          {transaction.katanaTxId ? (
            <IconWrap>
              <SuccessIcon />
            </IconWrap>
          ) : (
            <LabelClickable
              type="error"
              // onClick={txLink ? () => openExternalUrl(txLink) : undefined}
            >
              Pending
            </LabelClickable>
          )}
        </>
      )}
    </Wrap>
  );
}
